import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { formatAsRegionalPhoneCode, formatCnpj, formatCpf } from '@ibk/shared-utils';
import { AccountTypeAcronym, DocumentTypeDescription, PixType } from '../../enums';
import { formatAsBankAccountWithDigit, formatAsOnlyNumbers, formatAsRandomPixKey, formatPhoneNumber, removeFormatOfRandomPixKey, } from '../../utils/pixFormatters';
import { isCpf } from '../../utils/pixValidations';
import { useValidatePixKeyDetails } from '../useValidatePixKeyDetails';
export function usePixTransferSelectKeyType(externalKey, externalKeyType, externalAccountNumber, externalAgencyNumber, externalAccountType, externalFinancialInstitution, isRequiredIdentifierAndName) {
    const { t } = useTranslation();
    const { isAlphanumericEnabled, isValidCnpj, maskCnpj, unmaskCnpj } = useCnpjAlphanumeric();
    const validatePixKeyByPixKeyType = useCallback((keyType, value) => {
        if (!value || !keyType)
            return false;
        return {
            [PixType.TAX_IDENTIFIER]: isCpf(value) || isValidCnpj(value),
            [PixType.EMAIL]: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
            [PixType.PHONE]: value.length === 11,
            [PixType.RANDOM]: value.length === 32,
        }[keyType];
    }, [isValidCnpj]);
    const [selectedKeyType, setSelectedKeyType] = useState(externalKeyType);
    const [keyValue, setKeyValue] = useState(externalKey);
    const [pixKeyIsValid, setPixKeyIsValid] = useState(() => {
        if (externalKey && externalKeyType) {
            return validatePixKeyByPixKeyType(externalKeyType, externalKey);
        }
        return undefined;
    });
    const [identifierIsValid, setIdentifierIsValid] = useState(true);
    const [identifier, setIdentifier] = useState();
    const [nameTransfer, setNameTransfer] = useState();
    const [accountNumber, setAccountNumber] = useState(() => externalAccountNumber ? formatAsBankAccountWithDigit(externalAccountNumber) : externalAccountNumber);
    const [agencyNumber, setAgencyNumber] = useState(externalAgencyNumber);
    const [accountType, setAccountType] = useState(externalAccountType);
    const [selectedContact, setSelectedContact] = useState();
    const [selectedInstitution, setSelectedInstitution] = useState(externalFinancialInstitution);
    const { isValidating, pixKeyDetails, showInvalidKeyError, validatePixKey, clearInvalidKeyError } = useValidatePixKeyDetails();
    const keyTypeOptions = useMemo(() => [
        { value: PixType.EMAIL, label: t('pix-keys.email') },
        { value: PixType.TAX_IDENTIFIER, label: t('pix-keys.cpf-cnpj') },
        { value: PixType.PHONE, label: t('pix-keys-select.phone') },
        { value: PixType.RANDOM, label: t('pix-keys.random') },
        { value: PixType.AGENCY, label: t('pix-keys-select.agency-account') },
    ], [t]);
    const accountTypeOptions = useMemo(() => [
        { value: AccountTypeAcronym.CHECKING_ACCOUNT, label: t('pix-transfer-select-key-type-account-checking-account') },
        { value: AccountTypeAcronym.PAYMENT_ACCOUNT, label: t('pix-transfer-select-key-type-account-payment-account') },
        { value: AccountTypeAcronym.SAVING_ACCOUNT, label: t('pix-transfer-select-key-type-account-saving-account') },
    ], [t]);
    const isKeyTypeSelectedInput = useMemo(() => {
        return (selectedKeyType === PixType.EMAIL ||
            selectedKeyType === PixType.TAX_IDENTIFIER ||
            selectedKeyType === PixType.PHONE ||
            selectedKeyType === PixType.RANDOM);
    }, [selectedKeyType]);
    const isValid = useMemo(() => {
        return ((isKeyTypeSelectedInput && keyValue && pixKeyIsValid) ||
            (selectedKeyType === PixType.AGENCY &&
                accountNumber &&
                agencyNumber &&
                accountType &&
                selectedInstitution &&
                ((isRequiredIdentifierAndName && identifier && nameTransfer && identifierIsValid) ||
                    !isRequiredIdentifierAndName)) ||
            selectedContact);
    }, [
        isKeyTypeSelectedInput,
        keyValue,
        pixKeyIsValid,
        selectedKeyType,
        accountNumber,
        agencyNumber,
        accountType,
        selectedInstitution,
        isRequiredIdentifierAndName,
        identifier,
        nameTransfer,
        identifierIsValid,
        selectedContact,
    ]);
    const inputKeyInfo = useMemo(() => {
        switch (selectedKeyType) {
            case PixType.EMAIL:
                return {
                    label: t('pix-keys.email'),
                    placeholder: t('pix-transfer-select-key-type-email-placeholder'),
                    id: 'email',
                    type: 'email',
                    inputMode: 'email',
                    minLength: 1,
                    maxLength: 255,
                    errorMessage: t('pix-transfer-select-key-type-email-error'),
                    onChange: () => { },
                };
            case PixType.TAX_IDENTIFIER:
                return {
                    label: t('pix-keys.cpf-cnpj'),
                    placeholder: t('pix-transfer-select-key-type-cpf-cnpj-placeholder'),
                    id: 'cpf-cnpj',
                    type: 'text',
                    inputMode: isAlphanumericEnabled ? 'text' : 'numeric',
                    minLength: 14,
                    maxLength: 18,
                    errorMessage: t('pix-transfer-select-key-type-cpf-cnpj-error'),
                    onChange: () => { },
                };
            case PixType.PHONE:
                return {
                    label: t('pix-keys.phone'),
                    placeholder: t('pix-transfer-select-key-type-phone-placeholder'),
                    id: 'phone',
                    type: 'tel',
                    inputMode: 'numeric',
                    minLength: 14,
                    maxLength: 18,
                    errorMessage: t('pix-transfer-select-key-type-phone-error'),
                    onChange: () => { },
                };
            case PixType.RANDOM:
                return {
                    label: t('pix-keys.random'),
                    placeholder: t('pix-transfer-select-key-type-random-placeholder'),
                    id: 'random',
                    type: 'text',
                    inputMode: 'text',
                    minLength: 1,
                    maxLength: 36,
                    errorMessage: t('pix-transfer-select-key-type-random-error'),
                    onChange: () => { },
                };
            default:
                return null;
        }
    }, [selectedKeyType, t, isAlphanumericEnabled]);
    const resetKeyType = useCallback(() => {
        setKeyValue(undefined);
        setPixKeyIsValid(undefined);
        setSelectedKeyType(undefined);
        setAccountNumber(undefined);
        setAgencyNumber(undefined);
        setAccountType(undefined);
        setSelectedInstitution(undefined);
        setIdentifier(undefined);
        setNameTransfer(undefined);
        setIdentifierIsValid(true);
    }, []);
    useEffect(() => {
        if (selectedContact) {
            resetKeyType();
        }
    }, [selectedContact, resetKeyType]);
    useEffect(() => {
        if (selectedKeyType === PixType.AGENCY) {
            setKeyValue(undefined);
        }
        else {
            setAccountNumber(undefined);
            setAgencyNumber(undefined);
            setAccountType(undefined);
            setSelectedInstitution(undefined);
            setIdentifier(undefined);
            setNameTransfer(undefined);
            setIdentifierIsValid(true);
        }
    }, [selectedKeyType, setKeyValue, setAccountNumber, setAgencyNumber, setAccountType, setSelectedInstitution]);
    useEffect(() => {
        if (keyValue && selectedKeyType) {
            setPixKeyIsValid(validatePixKeyByPixKeyType(selectedKeyType, keyValue));
        }
    }, [keyValue, selectedKeyType, validatePixKeyByPixKeyType]);
    useEffect(() => {
        if (externalFinancialInstitution) {
            setSelectedInstitution(externalFinancialInstitution);
        }
    }, [externalFinancialInstitution]);
    const formatPixKeyByKeyType = useCallback((keyType, value) => {
        if (!value)
            return;
        if (!keyType)
            return value;
        const formatTaxIdentifier = (taxValue) => {
            if (taxValue.length <= 11)
                return formatCpf(taxValue);
            return isAlphanumericEnabled ? maskCnpj(taxValue) : formatCnpj(taxValue);
        };
        return {
            [PixType.TAX_IDENTIFIER]: formatTaxIdentifier(value),
            [PixType.EMAIL]: value.toLowerCase(),
            [PixType.PHONE]: formatPhoneNumber(value),
            [PixType.RANDOM]: formatAsRandomPixKey(value),
        }[keyType];
    }, [isAlphanumericEnabled, maskCnpj]);
    const formatPixKeyValidate = useCallback((keyType, value) => {
        if (!value)
            return;
        if (!keyType)
            return value;
        return {
            [PixType.TAX_IDENTIFIER]: isAlphanumericEnabled ? unmaskCnpj(value) : formatAsOnlyNumbers(value),
            [PixType.EMAIL]: value.toLowerCase(),
            [PixType.PHONE]: formatAsRegionalPhoneCode(value),
            [PixType.RANDOM]: formatAsRandomPixKey(value),
        }[keyType];
    }, [isAlphanumericEnabled, unmaskCnpj]);
    const removeFormatOfPixKeyByKeyType = useCallback((keyType, value) => {
        if (!value)
            return;
        if (!keyType)
            return value;
        return {
            [PixType.TAX_IDENTIFIER]: isAlphanumericEnabled ? unmaskCnpj(value) : formatAsOnlyNumbers(value),
            [PixType.EMAIL]: value.toLowerCase(),
            [PixType.PHONE]: formatAsOnlyNumbers(value.replace(/^(\+55|55)/, '')),
            [PixType.RANDOM]: removeFormatOfRandomPixKey(value),
        }[keyType];
    }, [isAlphanumericEnabled, unmaskCnpj]);
    const handleKeyValueChange = useCallback((keyType, value) => {
        const newValue = removeFormatOfPixKeyByKeyType(keyType, value);
        if (keyType === PixType.PHONE) {
            if (newValue.length > 11)
                return;
        }
        const validated = validatePixKeyByPixKeyType(keyType, newValue);
        setPixKeyIsValid(validated);
        setKeyValue(newValue);
    }, [validatePixKeyByPixKeyType, removeFormatOfPixKeyByKeyType]);
    const handleKeyTypeChange = useCallback((keyType) => {
        setSelectedKeyType(keyType);
        setKeyValue(undefined);
        setPixKeyIsValid(undefined);
        setSelectedContact(undefined);
    }, []);
    const handleKeyTypeClear = useCallback(() => {
        setSelectedKeyType(undefined);
        setSelectedContact(undefined);
    }, []);
    const resetSelection = useCallback(() => {
        setSelectedKeyType(undefined);
        setSelectedContact(undefined);
    }, []);
    useEffect(() => {
        if (selectedKeyType) {
            setSelectedContact(undefined);
        }
    }, [selectedKeyType]);
    const handleAccountTypeChange = useCallback((accountType) => {
        setAccountType(accountType);
    }, []);
    const handleContactChange = useCallback((contact) => {
        setSelectedContact(contact);
    }, []);
    const handleAgencyNumberChange = useCallback((agencyNumber) => {
        setAgencyNumber(formatAsOnlyNumbers(agencyNumber));
    }, []);
    const handleAccountNumberChange = useCallback((accountNumber) => {
        setAccountNumber(formatAsBankAccountWithDigit(accountNumber));
    }, []);
    const handleInstitutionChange = useCallback((institution) => {
        setSelectedInstitution(institution);
    }, []);
    const handleIdentifierChangeFormatted = useCallback((identifier) => {
        return formatPixKeyByKeyType(PixType.TAX_IDENTIFIER, identifier);
    }, [formatPixKeyByKeyType]);
    const handleIdentifierValueChange = useCallback((value) => {
        const newValue = removeFormatOfPixKeyByKeyType(PixType.TAX_IDENTIFIER, value);
        const validated = validatePixKeyByPixKeyType(PixType.TAX_IDENTIFIER, newValue);
        setIdentifierIsValid(validated);
        setIdentifier(newValue);
    }, [validatePixKeyByPixKeyType, removeFormatOfPixKeyByKeyType]);
    const handleNameChange = useCallback((name) => {
        setNameTransfer(name);
    }, []);
    const handleValidateAndProceed = useCallback(async () => {
        if (isKeyTypeSelectedInput && keyValue) {
            const formatPixKeyByKeyType = formatPixKeyValidate(selectedKeyType, keyValue);
            return await validatePixKey(formatPixKeyByKeyType);
        }
        if (selectedContact) {
            const pixKeyDetail = selectedContact.details.find(detail => detail.type === 1);
            if (pixKeyDetail?.data?.pixKey) {
                return await validatePixKey(pixKeyDetail.data.pixKey);
            }
        }
        return null;
    }, [formatPixKeyValidate, isKeyTypeSelectedInput, keyValue, selectedContact, selectedKeyType, validatePixKey]);
    const handleAgencyAccountFlow = useCallback((onKeyTypeSelect, onNext) => {
        if (selectedKeyType === PixType.AGENCY &&
            selectedInstitution &&
            nameTransfer &&
            identifier &&
            accountNumber &&
            agencyNumber &&
            accountType) {
            const isFromSavedContact = !!selectedContact;
            const agencyAccount = {
                bankAccount: accountNumber,
                bankName: selectedInstitution.name,
                bankBranch: agencyNumber,
                bankCode: selectedInstitution.code,
                accountType: accountType,
                name: nameTransfer,
                document: identifier,
                documentType: identifier?.length === 14 ? DocumentTypeDescription.CPF : DocumentTypeDescription.CNPJ,
            };
            onKeyTypeSelect(selectedKeyType, keyValue || '', null, isFromSavedContact, agencyAccount);
            onNext(keyValue, selectedKeyType, accountNumber, agencyNumber, accountType, selectedInstitution?.code);
            return true;
        }
        return false;
    }, [
        selectedKeyType,
        selectedInstitution,
        nameTransfer,
        identifier,
        accountNumber,
        agencyNumber,
        accountType,
        selectedContact,
        keyValue,
    ]);
    const handlePixKeyFlow = useCallback(async (onKeyTypeSelect, onNext) => {
        const pixKeyDetails = await handleValidateAndProceed();
        if (!pixKeyDetails)
            return false;
        let finalKeyType = selectedKeyType;
        let finalKeyValue = formatPixKeyValidate(selectedKeyType, keyValue);
        const isFromSavedContact = !!selectedContact;
        if (selectedContact && !keyValue) {
            const pixKeyDetail = selectedContact.details.find(detail => detail.type === 1);
            if (pixKeyDetail?.data?.pixKey && pixKeyDetail?.data?.pixKeyType) {
                finalKeyValue = pixKeyDetail.data.pixKey;
                finalKeyType = pixKeyDetail.data.pixKeyType;
            }
        }
        if (finalKeyType && finalKeyValue) {
            onKeyTypeSelect(finalKeyType, finalKeyValue, pixKeyDetails, isFromSavedContact);
            onNext(finalKeyValue, finalKeyType, accountNumber, agencyNumber, accountType, selectedInstitution?.code);
            return true;
        }
        return false;
    }, [
        handleValidateAndProceed,
        selectedKeyType,
        formatPixKeyValidate,
        keyValue,
        selectedContact,
        accountNumber,
        agencyNumber,
        accountType,
        selectedInstitution?.code,
    ]);
    const handleFallbackFlow = useCallback((onNext) => {
        onNext(keyValue, selectedKeyType, accountNumber, agencyNumber, accountType, selectedInstitution?.code);
    }, [keyValue, selectedKeyType, accountNumber, agencyNumber, accountType, selectedInstitution]);
    const handleButtonClick = useCallback(async (onNext, onKeyTypeSelect) => {
        if (onKeyTypeSelect) {
            const agencyFlowHandled = handleAgencyAccountFlow(onKeyTypeSelect, onNext);
            if (agencyFlowHandled)
                return;
            await handlePixKeyFlow(onKeyTypeSelect, onNext);
        }
        else {
            handleFallbackFlow(onNext);
        }
    }, [handleAgencyAccountFlow, handlePixKeyFlow, handleFallbackFlow]);
    return {
        selectedKeyType,
        keyTypeOptions,
        isValid,
        isKeyTypeSelectedInput,
        inputKeyInfo,
        keyValue,
        pixKeyIsValid,
        accountTypeOptions,
        accountNumber,
        agencyNumber,
        accountType,
        nameTransfer,
        identifier,
        selectedContact,
        selectedInstitution,
        formatPixKeyByKeyType,
        handleKeyValueChange,
        handleKeyTypeChange,
        handleKeyTypeClear,
        resetSelection,
        handleAgencyNumberChange,
        handleAccountNumberChange,
        handleAccountTypeChange,
        handleInstitutionChange,
        handleContactChange,
        handleIdentifierChangeFormatted,
        handleNameChange,
        handleIdentifierValueChange,
        identifierIsValid,
        isValidating,
        pixKeyDetails,
        showInvalidKeyError,
        handleValidateAndProceed,
        handleButtonClick,
        clearInvalidKeyError,
    };
}
